import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserSelector } from '../store/selectors/userSelector'
import { ReactComponent as Logo } from '../assets/svg/logo-new.svg'
import Icon from './icons'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { APP_ENV, KEYS, URLS } from '../_config'
import Container from '../elements/Container'
import Text from '../elements/Typo/Text'
import { useUserApi } from '../store/api/hooks/userApiHook'
import toast from 'react-hot-toast'
import NotificationElement from './notification'
import { useNotificationApi } from '../store/api/hooks/notificationApiHook'
import pusherService from '../services/pusherService'
import LanguageSelector from './LanguageSelector'
import { trackAction, clearRumUser, clearGlobalContext } from '../utils/analytics/rumTracking'
import { trackEvent } from '../_common/functions'

interface HeaderProps {
    hideLoginButton?: boolean
    hideSignupButton?: boolean
    darkLogo?: boolean
}

const Header: React.FC<HeaderProps> = ({ hideSignupButton = false, hideLoginButton = false, darkLogo = false }) => {
    const { t } = useTranslation()
    const user: any = useUserSelector()
    const navigate = useNavigate()
    const userApi: any = useUserApi()
    const notificationApi: any = useNotificationApi()
    const [readNitifications, setReadNitifications] = useState<any[] | null>(null)
    const [unreadNitifications, setUnreadNitifications] = useState<any[] | null>(null)

    const [backtoexperiments, setBacktoexperiments] = useState<string | null>(null)
    const [isOpen, setIsOpen] = useState(false)
    const businessOwner = JSON.parse(localStorage.getItem(KEYS.BUSINESS_OWNER) || '{}')
    const { pathname } = useLocation()

    //const profileImage: any = globalThis.localStorage.getItem('profileImage')

    const logout = () => {
        trackEvent('header_logout_clicked', { timestamp: new Date().toISOString() })
        trackAction('user_logged_out', {
            timestamp: new Date().toISOString(),
        })
        if (APP_ENV === 'production' || APP_ENV === 'qa') {
            clearGlobalContext()
            clearRumUser()
        }

        userApi.logout(
            {},
            (resp: any) => {
                void navigate(URLS.HOME)
            },
            (err: any) => {},
        )
    }

    const getNotifications = (read: boolean | null = null) => {
        const payload: any = {
            sort_by: '-created_at',
            page: 1,
            per_page: 100,
        }
        if (read !== null) {
            payload.is_read = read
        }

        notificationApi.getnotifications(
            payload,
            (resp: any) => {
                if (read === null) {
                    const _read: any[] = []
                    const _unread: any[] = []
                    resp.map((noti: any) => {
                        if (noti.is_read) {
                            _unread.push(noti)
                        } else {
                            _read.push(noti)
                        }
                    })
                    setReadNitifications(_read)
                    setReadNitifications(_unread)
                } else if (read) {
                    setReadNitifications(resp)
                } else {
                    setUnreadNitifications(resp)
                }
            },
            (err: any) => {
                // toast.error(err.message)
            },
        )
    }

    const toggleDropdown = () => {
        if (!isOpen) {
            trackEvent('header_notifications_opened', { unread_count: unreadNitifications?.length ?? 0 })
        }
        setIsOpen(!isOpen)
    }
    const clearallnotification = () => {
        trackEvent('header_notifications_clear_all', {})
        const payload = {}
        notificationApi.notificationdelete(
            payload,
            (resp: any) => {
                if (resp?.success) {
                    setReadNitifications([])
                    setUnreadNitifications([])
                }
            },
            (err: any) => {
                toast.error(err.message)
            },
        )
    }

    const markallreadnotification = () => {
        trackEvent('header_notifications_mark_all_read', {})
        const payload = {}
        notificationApi.notificationmarkallread(
            payload,
            (resp: any) => {
                getNotifications(true)
                getNotifications(false)
            },
            (err: any) => {
                toast.error(err.message)
            },
        )
    }

    // Helper function to determine back-to-experiments state
    const getBackToExperiments = (segments: string[]): string | null => {
        return segments[0] === 'control-center' ? '1' : null
    }

    const getProfileImage = () => {
        /* if (profileImage) {
            return <img src={profileImage} alt="Profile" className="w-[1.083rem] h-[1.083rem] rounded-full" />
        } */
        return (
            <div className="text-[0.5rem] font-semibold bg-primary text-secondary w-[1.083rem] h-[1.083rem] rounded-full leading-none flex items-center justify-center">
                {user?.first_name?.charAt(0)}
                {user?.last_name?.charAt(0)}
            </div>
        )
    }

    useEffect(() => {
        const segments: string[] = pathname?.split('/').filter(Boolean)

        setBacktoexperiments(getBackToExperiments(segments))
    }, [pathname])

    const onNotification = (data: any) => {
        console.log('Received notification:', data)
        getNotifications(true)
        getNotifications(false)
        toast(
            (t) => (
                <div className="flex flex-col gap-3 items-center justify-center">
                    <Text size="lg">{data?.description}</Text>
                </div>
            ),
            { duration: 5000 },
        )
        if (data?.rct_executed && data?.rct_executed === true) {
            trackEvent('notification_rct_push_received', { experiment_literal: data?.experiment_literal })
            setTimeout(() => {
                const channel = new BroadcastChannel('RCT_RESPONSE')
                channel.postMessage({ type: 'RCT', id: data?.experiment_literal })
                channel.close()
            }, 3500)
        }
        if (data?.is_observational_study && data?.is_observational_study === true) {
            setTimeout(() => {
                const channel = new BroadcastChannel('OSTUDY_RESPONSE')
                channel.postMessage({ type: 'OSTUDY', id: data?.experiment_literal })
                channel.close()
            }, 3500)
        }

        if (data?.is_measurement && data?.is_measurement === true) {
            trackEvent('notification_measurement_push_received', { experiment_literal: data?.experiment_literal })
            setTimeout(() => {
                const channel = new BroadcastChannel('MEASURMENT_RESPONSE')
                channel.postMessage({ type: 'MEASURMENT', id: data?.experiment_literal })
                channel.close()
            }, 1500)
        }

        if (data?.event_type && data?.event_type == 'experiment_review') {
            trackEvent('experiment_review_realtime_update_received', { experiment_literal: data?.experiment_literal })
            const channel = new BroadcastChannel('EXPERIMENT_REVIEW')
            channel.postMessage({ type: 'EXPERIMENT_REVIEW', id: data?.experiment_literal })
            channel.close()
        }
    }

    useEffect(() => {
        getNotifications(true)
        getNotifications(false)

        pusherService.bind('notification', onNotification)
        return () => {
            pusherService.unbind('notification', onNotification)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <header className="bg-white w-full z-10">
            <Container className={`h-[2.273rem]  relative flex justify-between px-2 items-center  ${hideSignupButton ? ' bg-white ' : ''}`}>
                {pathname === '/my-experiments' ? (
                    <button
                        type="button"
                        onClick={() => {
                            trackEvent('header_logo_clicked', { current_page: pathname })
                            globalThis.location.reload()
                        }}
                        className="flex"
                        aria-label={t('Reload Page')}
                    >
                        <Logo width={'5.727rem'} height={'1.8648rem'} />
                    </button>
                ) : (
                    <Link to={URLS.MY_EXPERIMENTS} className="flex" onClick={() => trackEvent('header_logo_clicked', { current_page: pathname })}>
                        <Logo width={'5.727rem'} height={'1.8648rem'} />
                    </Link>
                )}
                {hideSignupButton ? null : (
                    <div className="flex gap-3 items-center">
                        <LanguageSelector />

                        {backtoexperiments ? (
                            <Link to={URLS.MY_EXPERIMENTS} className="flex items-center gap-1 bg-black rounded-md text-white py-1 px-2 text-[0.541rem] leading-none">
                                <Icon name="arrow-left" size={0.833} className="text-primary" /> <span className="text-[0.581rem]">{t('Back to Experiments')}</span>
                            </Link>
                        ) : null}

                        <div className="dropdown dropdown-end">
                            <button type="button" data-testid="bell-btn" className="relative mt-2 mr-2" tabIndex={0} onClick={toggleDropdown} onKeyDown={toggleDropdown}>
                                {unreadNitifications?.length !== 0 && (
                                    <span className="absolute left-[0.5rem] top-[-0.2rem] bg-accent rounded-full text-white text-[0.443rem] py-[0.1rem] leading-none px-1 font-medium">{unreadNitifications?.length}</span>
                                )}
                                <Icon name="bell" className="text-[#74788D]" size={0.833} />
                            </button>
                            {isOpen && (
                                <div className="dropdown-content flex flex-col gap-[0.833rem] z-[1] w-[31.208rem]  bg-white shadow-1 rounded-[0.833rem] p-5 mt-[0.333rem]">
                                    <div className="flex items-center justify-between">
                                        <p className="text-[1.042rem] leading-[1.563rem] font-bold">{t('Notification')}</p>
                                        <div className="flex items-center gap-[0.417rem]">
                                            <button type="button" className="flex items-center gap-[0.333rem]">
                                                <Icon size={0.583} name="envelop-open" />
                                                <Text onClick={markallreadnotification} className="font-semibold" size="14">
                                                    {t('Mark all as Read')}
                                                </Text>
                                            </button>
                                            <button type="button" className="flex items-center gap-[0.333rem]">
                                                <Icon size={0.583} name="delete" />
                                                <Text onClick={clearallnotification} className="font-semibold" size="14">
                                                    {t('Clear all')}
                                                </Text>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-[0.833rem]">
                                        {readNitifications?.length == 0 && unreadNitifications?.length == 0 ? (
                                            <Text size="md" className="font-medium opacity-50 !leading-none text-center">
                                                {t('You have no notifications')}
                                            </Text>
                                        ) : (
                                            <div className="max-h-[22rem] overflow-auto flex flex-col gap-[0.833rem] -mx-[1.25rem] px-[1.25rem]">
                                                {unreadNitifications?.map((noti: any) => (
                                                    <React.Fragment key={`noti-${noti.id}`}>
                                                        <NotificationElement data={noti} />
                                                    </React.Fragment>
                                                ))}
                                                {readNitifications && readNitifications?.length > 0 && (
                                                    <Text size="md" className="font-medium opacity-50 !leading-none">
                                                        {t('Read Notifications')}
                                                    </Text>
                                                )}
                                                {readNitifications?.map((noti: any) => (
                                                    <React.Fragment key={`noti-${noti.id}`}>
                                                        <NotificationElement data={noti} />
                                                    </React.Fragment>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="dropdown dropdown-end">
                            <button type="button" tabIndex={0} className="bg-[#74788D] rounded-[1.25rem] p-[0.125rem] flex gap-1 items-center" onClick={() => trackEvent('header_user_menu_opened', {})}>
                                {getProfileImage()}

                                <Icon name="down" size={0.667} className="text-white" />
                            </button>
                            <ul className="dropdown-content z-[1] min-w-[8rem] bg-white shadow-md rounded-b-[0.833rem] px-[1.563rem] py-[0.5rem]">
                                <li className="my-2">
                                    <Link to={URLS.PROFILE} className="flex items-center gap-2" onClick={() => trackEvent('header_profile_link_clicked', {})}>
                                        <Icon name="profile" size={0.833} />
                                        <Text size="sm" className="font-semibold">
                                            {t('Profile')}
                                        </Text>
                                    </Link>
                                </li>
                                {user?.is_admin || businessOwner ? (
                                    <li className="my-2">
                                        <Link to={URLS.CONTROL_CENTER('all-experiments')} className="flex items-center gap-2" onClick={() => trackEvent('header_experiment_vault_clicked', {})}>
                                            <svg width="0.833rem" height="0.833rem" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                                <path
                                                    d="M16.5 0H1.5C1.10218 0 0.720644 0.158035 0.43934 0.43934C0.158035 0.720644 0 1.10218 0 1.5V16.5C0 16.8978 0.158035 17.2794 0.43934 17.5607C0.720644 17.842 1.10218 18 1.5 18H16.5C16.8978 18 17.2794 17.842 17.5607 17.5607C17.842 17.2794 18 16.8978 18 16.5V1.5C18 1.10218 17.842 0.720644 17.5607 0.43934C17.2794 0.158035 16.8978 0 16.5 0ZM16.5 1.5V11.25H13.8094C13.6125 11.2495 13.4174 11.2881 13.2356 11.3636C13.0537 11.4391 12.8887 11.5499 12.75 11.6897L10.9397 13.5H7.06031L5.25 11.6897C5.11122 11.5498 4.94601 11.4389 4.76398 11.3634C4.58196 11.2879 4.38674 11.2494 4.18969 11.25H1.5V1.5H16.5ZM16.5 16.5H1.5V12.75H4.18969L6 14.5603C6.13878 14.7002 6.30399 14.8111 6.48602 14.8866C6.66804 14.9621 6.86326 15.0006 7.06031 15H10.9397C11.1367 15.0006 11.332 14.9621 11.514 14.8866C11.696 14.8111 11.8612 14.7002 12 14.5603L13.8103 12.75H16.5V16.5ZM5.46937 8.78063C5.32864 8.63989 5.24958 8.44902 5.24958 8.25C5.24958 8.05098 5.32864 7.86011 5.46937 7.71937C5.61011 7.57864 5.80098 7.49958 6 7.49958C6.19902 7.49958 6.38989 7.57864 6.53063 7.71937L8.25 9.43969V3.75C8.25 3.55109 8.32902 3.36032 8.46967 3.21967C8.61032 3.07902 8.80109 3 9 3C9.19891 3 9.38968 3.07902 9.53033 3.21967C9.67098 3.36032 9.75 3.55109 9.75 3.75V9.43969L11.4694 7.71937C11.5391 7.64969 11.6218 7.59442 11.7128 7.5567C11.8039 7.51899 11.9015 7.49958 12 7.49958C12.0985 7.49958 12.1961 7.51899 12.2872 7.5567C12.3782 7.59442 12.4609 7.64969 12.5306 7.71937C12.6003 7.78906 12.6556 7.87178 12.6933 7.96283C12.731 8.05387 12.7504 8.15145 12.7504 8.25C12.7504 8.34855 12.731 8.44613 12.6933 8.53717C12.6556 8.62822 12.6003 8.71094 12.5306 8.78063L9.53063 11.7806C9.46097 11.8504 9.37825 11.9057 9.2872 11.9434C9.19616 11.9812 9.09856 12.0006 9 12.0006C8.90144 12.0006 8.80384 11.9812 8.7128 11.9434C8.62175 11.9057 8.53903 11.8504 8.46937 11.7806L5.46937 8.78063Z"
                                                    fill="black"
                                                />
                                            </svg>
                                            <Text size="sm" className="font-semibold whitespace-nowrap">
                                                {t('Experiment Vault')}
                                            </Text>
                                        </Link>
                                    </li>
                                ) : null}
                                {user?.is_admin ? (
                                    <li className="my-2">
                                        <Link to={URLS.ADMIN} className="flex items-center gap-2" onClick={() => trackEvent('header_admin_link_clicked', {})}>
                                            <Icon name="user" size={0.833} />
                                            <Text size="sm" className="font-semibold">
                                                {t('Admin')}
                                            </Text>
                                        </Link>
                                    </li>
                                ) : null}
                                {/* Agents is dev-only: shown on localhost + dev build, hidden in qa/stg/production.
                                    REACT_APP_ENV is unset on localhost (start:dev), so we exclude-list deployed envs
                                    rather than match `=== 'dev'`. Keep in sync with routers.tsx showAgentFeatures. */}
                                {user?.is_admin && APP_ENV !== 'production' && APP_ENV !== 'qa' && APP_ENV !== 'stg' ? (
                                    <li className="my-2">
                                        <Link to={URLS.AGENTS} className="flex items-center gap-2">
                                            <svg width="0.833rem" height="0.833rem" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                                                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                            <Text size="sm" className="font-semibold whitespace-nowrap">
                                                {t('Agents')}
                                            </Text>
                                        </Link>
                                    </li>
                                ) : null}

                                <li className="my-2">
                                    <button type="button" onClick={logout} className="flex items-center gap-2 w-full text-left font-semibold text-sm cursor-pointer" data-testid="logout-btn">
                                        <Icon name="logout" size={0.833} />
                                        <Text size="sm" className="font-semibold">
                                            {t('Logout')}
                                        </Text>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </Container>
        </header>
    )
}

export default Header
